import React, { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import { Grid } from '@mui/material'
import useSubAdmin from './services/hooks/useSubAdmin'
import useCreateSubAdmins from './services/hooks/useCreateSubAdmins'
import useSubAdminEnabled from './services/hooks/useSubAdminEnabled'
import SubAdminTable from './component/SubAdminTable/index'
import Loader from '../../components/Loader'
import TaskModal from '../../components/Modal'
import Popup from '../../components/Common/Popup'
import CustomPageNavigator from '../../components/Pagination/index'
import SubAdminModalContent from './component/SubAdminModal/SubAdminModalContent'
import cacheKeys from '../../services/cacheKeys'
import { SubAdmin } from '../../services/types/APIRequestType'
import { FormValues } from '../../services/types/APIRequestType'
import { invalidateQuery } from '../../services/config/queryClient'
import { AdminRole, ApiStatusConstants } from '../../utils/config/auth-enums'
import { unformattedPhoneNumber, validatePhoneNumber } from '../../utils'
import './ManageSubAdmin.css'
import useValidation from '../../hooks/useValidation'
import { styled, alpha } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import InputBase from '@mui/material/InputBase'
import DataUnavailable from '@/components/DataUnavailable'

const ManageSubAdmin = () => {
  const [open, setOpen] = React.useState<boolean>(false)
  const [apiStatus, setApiStatus] = useState<ApiStatusConstants>(
    ApiStatusConstants.INITIAL,
  )
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const [admins, setAdmins] = useState<SubAdmin[] | undefined>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [formValues, setFormValues] = useState<FormValues>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  })
  const [searchValue, setSearchValue] = useState('')
  const [debouncedValue, setDebouncedValue] = useState(searchValue)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isEnabled, setIsEnabled] = useState<boolean>()
  const [isCreated, setIsCreated] = useState<boolean>()
  const itemsPerPage = 5

  const Search = styled('div')(({ theme }) => ({
    display: 'flex',
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginLeft: 0,
    width: '50%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  }))

  const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }))

  const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    width: '100%',
    '& .MuiInputBase-input': {
      padding: theme.spacing(1, 1, 1, 0),
      paddingLeft: `calc(1em + ${theme.spacing(4)})`,
      transition: theme.transitions.create('width'),
      [theme.breakpoints.up('sm')]: {
        width: '23ch',
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  }))

  const inputValidationRules = {
    firstName: [
      { required: true, message: 'Please enter your first name.' },
      {
        pattern: /^[a-zA-Z\s]+$/,
        message: 'First name can only contain letters.',
      },
    ],
    lastName: [
      { required: true, message: 'Please enter your last name.' },
      {
        pattern: /^[a-zA-Z\s]+$/,
        message: 'Last name can only contain letters.',
      },
    ],
  }

  const { errors, validateInputs, setErrors } = useValidation(
    {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
    },
    inputValidationRules,
  )
  const { data: subAdmins, isLoading } = useSubAdmin(
    currentPage,
    itemsPerPage,
    debouncedValue,
  )
  const { mutate: createSubAdmins, isPending } = useCreateSubAdmins()
  const { mutate: subAdminEnabled, isPending: resetAdmin } =
    useSubAdminEnabled()

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setFormValues({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    })
    setSelectedId(null)
    setIsEdit(false)
    setErrorMsg('')
    setErrors({})
  }

  const handleContinue = () => {
    setShowPopup(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    if (name === 'phone') {
      const unformattedNumbers = unformattedPhoneNumber(value, '+1')
      setFormValues({
        ...formValues,
        [name]: unformattedNumbers,
      })
    } else {
      setFormValues({
        ...formValues,
        [name]: value,
      })
    }
  }

  const handleSubmit = () => {
    if (!validateInputs()) {
      return
    }
    setApiStatus(ApiStatusConstants.LOADING)
    const { isValid, errorMessage } = validatePhoneNumber(formValues.phone!)
    if (!isValid) {
      setApiStatus(ApiStatusConstants.FAILURE)
      setErrorMsg(errorMessage)
      return
    }
    const payload = {
      role: AdminRole.SUB_ADMIN,
      ...formValues,
    }

    createSubAdmins(payload, {
      onSuccess: () => {
        setApiStatus(ApiStatusConstants.SUCCESS)
        invalidateQuery([cacheKeys.SUB_ADMINS])
        setCurrentPage(1)
        setIsCreated(true)
        setShowPopup(true)
        setFormValues({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
        })
        setOpen(false)
      },
      onError: (error) => {
        setApiStatus(ApiStatusConstants.FAILURE)
        setErrorMsg(error.message || 'Something went wrong. Please try again.')
      },
    })
  }

  const handleCredentials = (id: string | null) => {
    const payload = {
      _id: id,
      accountStatus: isEnabled ? false : true,
    }

    subAdminEnabled(payload, {
      onSuccess: () => {
        handleClose()
        setShowPopup(true)
        invalidateQuery([cacheKeys.SUB_ADMINS])
      },
      onError: (error) => {
        console.error(error)
      },
    })
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log(e.target.value)
    setSearchValue(e.target.value)
  }

  const renderErrorMsg = () => {
    if (apiStatus === ApiStatusConstants.FAILURE) {
      return <p className="error-msg">{errorMsg}</p>
    }
    return null
  }

  useEffect(() => {
    setAdmins(subAdmins?.data?.subAdminProfiles)
  }, [subAdmins])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchValue)
    }, 400)

    return () => {
      clearTimeout(handler)
    }
  }, [searchValue])

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      <div className="dashbord-filter">
        <div
          style={{
            display: 'flex',
            marginRight: '20px',
            height: '50px',
          }}
        >
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ 'aria-label': 'search' }}
              value={searchValue}
              onChange={handleSearchChange}
              autoFocus={!open}
            />
          </Search>
        </div>
        <Button onClick={handleOpen}>
          <img alt="back" src="/icon/plus-icon.svg" />
        </Button>
      </div>

      <div>
        {admins && admins.length > 0 ? (
          <>
            <SubAdminTable
              currentItems={admins}
              handleOpen={handleOpen}
              setIsEdit={setIsEdit}
              setFormValues={setFormValues}
              setSelectedId={setSelectedId}
              setIsEnabled={setIsEnabled}
            />

            <CustomPageNavigator
              currentPage={currentPage}
              totalItemCount={subAdmins?.data?.totalCount || 0}
              limitPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
            />
          </>
        ) : (
          <DataUnavailable description="No record found" />
        )}
        <TaskModal
          content={
            <SubAdminModalContent
              open={open}
              handleClose={handleClose}
              isEdit={isEdit}
              formValues={formValues}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              isPending={isPending}
              renderErrorMsg={renderErrorMsg}
              selectedId={selectedId}
              isEnabled={isEnabled}
              resetAdmin={resetAdmin}
              handleCredentials={handleCredentials}
              errors={errors}
            />
          }
          open={open}
          handleClose={handleClose}
        />
        <Grid>
          {showPopup && (
            <Popup
              title={
                isCreated ? (
                  <>
                    Sub Admin has <br />
                    been created successfully!
                  </>
                ) : isEnabled ? (
                  <>
                    User and Password <br />
                    generated successfully!
                  </>
                ) : (
                  <>
                    Sub Admin has <br />
                    been successfully disabled.
                  </>
                )
              }
              buttonText="Continue"
              onButtonClick={handleContinue}
            />
          )}
        </Grid>
      </div>
    </>
  )
}

export default ManageSubAdmin
